import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ClientResponseError } from 'pocketbase'
import { pb } from '@/lib/pocketbase'
import type {
  UserBookmarkedTender,
  UserBookmarkedAgency,
  Tender,
  Agency,
} from '@/lib/pocketbase'

type TenderBookmarkState = {
  isBookmarked: boolean
  bookmarkId?: string
}

const EMPTY_TENDER_BOOKMARK_STATE: TenderBookmarkState = {
  isBookmarked: false,
}

const BOOKMARKED_TENDERS_QUERY_KEY = ['bookmarked-tenders'] as const

function isNotFoundError(error: unknown) {
  return error instanceof ClientResponseError && error.status === 404
}

async function findBookmarkedTender(userId: string, tenderId: string) {
  try {
    return await pb.collection('user_bookmarked_tenders').getFirstListItem<UserBookmarkedTender>(
      pb.filter('user = {:user} && tender = {:tender}', {
        user: userId,
        tender: tenderId,
      }),
      {
        expand: 'tender',
      }
    )
  } catch (error) {
    if (isNotFoundError(error)) {
      return null
    }
    throw error
  }
}

// Fetch user's bookmarked tenders
export function useBookmarkedTenders() {
  return useQuery({
    queryKey: BOOKMARKED_TENDERS_QUERY_KEY,
    queryFn: async () => {
      const userId = pb.authStore.model?.id
      if (!userId) return []

      const result = await pb.collection('user_bookmarked_tenders').getList<UserBookmarkedTender>(1, 100, {
        filter: pb.filter('user = {:user}', { user: userId }),
        expand: 'tender',
      })

      return result.items.map((item) => {
        const tenderExpand = (item.expand as any)?.tender
        return {
          bookmark: item,
          tender: tenderExpand
            ? ({
                ...tenderExpand,
                project_name:
                  tenderExpand.project_name ||
                  tenderExpand.title_th ||
                  tenderExpand.egp_project_no ||
                  '',
              } as Tender)
            : undefined,
        }
      }).filter((item) => item.tender)
    },
  })
}

// Fetch user's bookmarked agencies
export function useBookmarkedAgencies() {
  return useQuery({
    queryKey: ['bookmarked-agencies'],
    queryFn: async () => {
      const userId = pb.authStore.model?.id
      if (!userId) return []

      const result = await pb.collection('user_bookmarked_agencies').getList<UserBookmarkedAgency>(1, 100, {
        filter: `user = "${userId}"`,
        expand: 'agency',
      })

      return result.items.map((item) => {
        const agencyExpand = (item.expand as any)?.agency
        return {
          bookmark: item,
          agency: agencyExpand
            ? ({
                ...agencyExpand,
                name:
                  agencyExpand.name ||
                  agencyExpand.name_th ||
                  agencyExpand.name_en ||
                  '',
              } as Agency)
            : undefined,
        }
      }).filter((item) => item.agency)
    },
  })
}

// Resolve whether a tender is bookmarked
export function useTenderBookmarkState(tenderId?: string) {
  const userId = pb.authStore.model?.id

  return useQuery({
    queryKey: ['tender-bookmark-state', userId, tenderId],
    enabled: !!userId && !!tenderId,
    queryFn: async (): Promise<TenderBookmarkState> => {
      if (!userId || !tenderId) {
        return EMPTY_TENDER_BOOKMARK_STATE
      }

      const tenderBookmark = await findBookmarkedTender(userId, tenderId)
      if (tenderBookmark) {
        return {
          isBookmarked: true,
          bookmarkId: tenderBookmark.id,
        }
      }

      return EMPTY_TENDER_BOOKMARK_STATE
    },
  })
}

// Toggle bookmark from a tender context
export function useToggleTenderBookmark() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ tenderId }: { tenderId: string }) => {
      const userId = pb.authStore.model?.id
      if (!userId) throw new Error('Not authenticated')

      const tenderBookmark = await findBookmarkedTender(userId, tenderId)
      if (tenderBookmark) {
        await pb.collection('user_bookmarked_tenders').delete(tenderBookmark.id)
        return
      }

      await pb.collection('user_bookmarked_tenders').create({
        user: userId,
        tender: tenderId,
      })
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: BOOKMARKED_TENDERS_QUERY_KEY }),
        queryClient.invalidateQueries({
          queryKey: ['tender-bookmark-state', pb.authStore.model?.id, variables.tenderId],
        }),
      ])
    },
  })
}

// Bookmark an agency
export function useBookmarkAgency() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (agencyId: string) => {
      const userId = pb.authStore.model?.id
      if (!userId) throw new Error('Not authenticated')

      await pb.collection('user_bookmarked_agencies').create({
        user: userId,
        agency: agencyId,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarked-agencies'] })
    },
  })
}

// Unbookmark an agency
export function useUnbookmarkAgency() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (bookmarkId: string) => {
      await pb.collection('user_bookmarked_agencies').delete(bookmarkId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarked-agencies'] })
    },
  })
}
